import { useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { StatusBadge } from "../../components/site/Cards";
import { AdminHead } from "./AdminLayout";
import { ImageUpload, Field } from "./ImageUpload";
import { api, img, BREEDS, STATUSES, useAction, useGet } from "../../lib/api";

const EMPTY = { name: "", breed: "Limousin", weight_estimate: "", description: "", image_url: "", status: "Tersedia" };
const pick = ({ name, breed, weight_estimate, description, image_url, status }) => ({ name, breed, weight_estimate, description, image_url, status });

const Sel = ({ value, onChange, options, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent>{options.map((o) => <SelectItem key={o} value={o} data-testid={`${testid}-${o}`}>{o}</SelectItem>)}</SelectContent>
  </Select>
);

const CattleForm = ({ initial, onSave }) => {
  const [f, setF] = useState(initial);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  return (
    <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); onSave(f); }}>
      <ImageUpload value={f.image_url} onChange={set("image_url")} testid="cattle-image-upload" aspect="aspect-[4/3]" />
      <Field label="Nama / Kode Sapi"><Input value={f.name} onChange={set("name")} required data-testid="cattle-name-input" /></Field>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Kategori Ras"><Sel value={f.breed} onChange={set("breed")} options={BREEDS} testid="cattle-breed-select" /></Field>
        <Field label="Status"><Sel value={f.status} onChange={set("status")} options={STATUSES} testid="cattle-status-select" /></Field>
      </div>
      <Field label="Estimasi Bobot"><Input placeholder="± 500 kg" value={f.weight_estimate} onChange={set("weight_estimate")} data-testid="cattle-weight-input" /></Field>
      <Field label="Deskripsi"><Textarea rows={3} value={f.description} onChange={set("description")} data-testid="cattle-description-input" /></Field>
      <button type="submit" disabled={!f.image_url} className="btn-forest w-full disabled:opacity-50" data-testid="cattle-save-button">Simpan Sapi</button>
    </form>
  );
};

export default function AdminCattle() {
  const { data } = useGet("cattle", "/cattle");
  const act = useAction(["cattle", "stats"]);
  const [editing, setEditing] = useState(null);

  const save = async (f) => {
    const ok = await act(() => (editing.id ? api.put(`/cattle/${editing.id}`, f) : api.post("/cattle", f)), "Data sapi disimpan");
    if (ok) setEditing(null);
  };
  const setStatus = (c, status) => act(() => api.put(`/cattle/${c.id}`, { ...pick(c), status }), `Status diubah ke ${status}`);
  const remove = (c) => window.confirm(`Hapus ${c.name}?`) && act(() => api.delete(`/cattle/${c.id}`), "Sapi dihapus");

  return (
    <>
      <AdminHead title="Katalog Sapi" desc="Tambah, ubah status, dan kelola data sapi.">
        <button className="btn-gold" onClick={() => setEditing(EMPTY)} data-testid="cattle-add-button"><Plus className="h-4 w-4" /> Tambah Sapi</button>
      </AdminHead>
      <div className="space-y-3">
        {(data || []).map((c) => (
          <div key={c.id} className="flex flex-wrap items-center gap-4 rounded-2xl border bg-white p-4" data-testid={`admin-cattle-row-${c.id}`}>
            <img src={img(c.image_url)} alt="" className="h-16 w-20 rounded-xl object-cover" />
            <div className="min-w-[160px] flex-1">
              <p className="font-semibold">{c.name}</p>
              <p className="text-xs text-[var(--ink)]/60">{c.breed} · {c.weight_estimate}</p>
            </div>
            <StatusBadge status={c.status} testid={`admin-cattle-badge-${c.id}`} />
            <div className="w-36"><Sel value={c.status} onChange={(v) => setStatus(c, v)} options={STATUSES} testid={`admin-cattle-status-${c.id}`} /></div>
            <button onClick={() => setEditing(c)} className="rounded-full p-2 hover:bg-[var(--cream)]" data-testid={`cattle-edit-${c.id}`}><Pencil className="h-4 w-4" /></button>
            <button onClick={() => remove(c)} className="rounded-full p-2 text-red-600 hover:bg-red-50" data-testid={`cattle-delete-${c.id}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        {data?.length === 0 && <p className="text-[var(--ink)]/60">Belum ada data sapi.</p>}
      </div>
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto" data-testid="cattle-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit Sapi" : "Tambah Sapi"}</DialogTitle></DialogHeader>
          {editing && <CattleForm initial={pick(editing)} onSave={save} />}
        </DialogContent>
      </Dialog>
    </>
  );
}
